/**
 * Index `/auth/*` minimal fetch clients (member.yaml).
 */

import { HEADERS, ospRegBrandHeaders } from "./constants";
import type { OspAuthApiConfig } from "./types";
import { unwrapOspFetchResponse } from "./wrapperResult";

/** Aligns with member.yaml `AccessToken` / SDK `AccessToken`. */
export interface OspAccessToken {
  access_token?: string;
  refresh_token?: string;
}

export interface OspIdTokenPayload {
  id_token?: string;
}

interface AuthLoginRequestBody {
  eoa_address: string;
  signature: string;
  issue_platform?: string;
  issue_channel?: string;
}

function authAppHeaders(config: OspAuthApiConfig): Record<string, string> {
  return {
    [HEADERS.APP_ID]: config.appId,
    ...ospRegBrandHeaders(),
  };
}

function authUrl(config: OspAuthApiConfig, path: string): string {
  return `${config.indexBaseUrl}${path}`;
}

export async function postAuthRefresh(
  config: OspAuthApiConfig,
  refreshToken: string,
): Promise<OspAccessToken> {
  const query = new URLSearchParams({
    refresh_token: refreshToken,
  });
  const url = `${authUrl(config, "/auth/refresh")}?${query.toString()}`;

  const response = await fetch(url, {
    method: "POST",
    headers: authAppHeaders(config),
  });

  return unwrapOspFetchResponse<OspAccessToken>(response);
}

export async function postAuthLogin(
  config: OspAuthApiConfig,
  eoa_address: string,
  signature: string,
  options?: Record<string, string> & { issue_platform?: string },
): Promise<OspAccessToken> {
  const body: AuthLoginRequestBody = {
    eoa_address,
    signature,
  };
  const headers: Record<string, string> = {
    ...authAppHeaders(config),
    "Content-Type": "application/json",
  };

  if (options) {
    const { issue_platform, ...rest } = options;
    if (issue_platform) {
      body.issue_platform = issue_platform;
    }
    Object.assign(headers, rest);
  }

  const response = await fetch(authUrl(config, "/auth/login"), {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  return unwrapOspFetchResponse<OspAccessToken>(response);
}

export async function getAuthIdToken(
  config: OspAuthApiConfig,
  accessToken: string,
): Promise<OspIdTokenPayload> {
  const response = await fetch(authUrl(config, "/auth/id_token"), {
    method: "GET",
    headers: {
      ...authAppHeaders(config),
      authorization: `Bearer ${accessToken}`,
    },
  });

  return unwrapOspFetchResponse<OspIdTokenPayload>(response);
}
